import type { ICustomMessage } from '@/components/chat';
import type { InquiryParticipant } from '@/hooks/useChat';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { isAskAnonymously } from '@/util/userFilters';
import { isSameUserId } from '@/utils/isSameUserId';

export type InquiryTitleExpert = Pick<
  InquiryParticipant,
  'id' | 'firstName' | 'lastName' | 'identifiesAsDoctor' | 'avatar' | 'specialty'
> & {
  userType?: string;
};

type InquiryWithDisplayMetadata = Pick<InquiryV2, 'anonymous' | 'recipient' | 'title'>;

type InquiryWithOptionalGroupInquiries = InquiryWithDisplayMetadata & {
  groupInquiries?: readonly { title?: InquiryV2['title'] | null }[];
};

function getParticipantDisplayName(participant?: InquiryParticipant | null) {
  return [participant?.firstName, participant?.lastName].filter(Boolean).join(' ');
}

function snapshotFromTitleExpertPayload(
  expert: NonNullable<NonNullable<InquiryV2['title']>['expert']>
): InquiryTitleExpert {
  return {
    id: expert.id,
    firstName: expert.firstName ?? null,
    lastName: expert.lastName ?? null,
    identifiesAsDoctor: expert.identifiesAsDoctor ?? false,
    avatar: expert.avatar as InquiryParticipant['avatar'],
    specialty: expert.specialty ?? null,
    userType: expert.userType,
  };
}

/** True when the inquiry is routed to the Ask Anonymously inbox rather than a direct expert. */
export function isAskAnonymouslyRoutedInquiry(
  inquiry: Pick<InquiryV2, 'recipient'> | null | undefined
): boolean {
  if (!inquiry?.recipient) {
    return false;
  }

  return isAskAnonymously(getParticipantDisplayName(inquiry.recipient));
}

/**
 * True when the Ask Anonymously account authored the inquiry: the expert-side thread staff
 * forward a question into, as opposed to the submitter-side thread it was routed to.
 */
export function isAskAnonymouslyAuthoredInquiry(
  inquiry: Pick<InquiryV2, 'author'> | null | undefined
): boolean {
  if (!inquiry?.author) {
    return false;
  }

  return isAskAnonymously(getParticipantDisplayName(inquiry.author));
}

/**
 * Which side of an Ask Anonymously inquiry a Primum user is looking at: `inbox` is the thread
 * routed to Ask Anonymously (the viewer is not its author), `expert` is the thread Ask
 * Anonymously forwarded to an expert (the viewer is acting as its author), `null` is neither.
 */
export type AskAnonymouslySide = 'inbox' | 'expert';

export function getAskAnonymouslySide(
  inquiry: Pick<InquiryV2, 'author' | 'recipient'> | null | undefined,
  { isAuthor }: { isAuthor: boolean }
): AskAnonymouslySide | null {
  if (isAskAnonymouslyAuthoredInquiry(inquiry)) {
    return 'expert';
  }

  if (!isAuthor && isAskAnonymouslyRoutedInquiry(inquiry)) {
    return 'inbox';
  }

  return null;
}

/**
 * True when the inquiry is anonymous and the conversation recipient is the Ask Anonymously
 * routing user rather than a direct expert thread.
 */
export function isAnonymousPrimumRoutedInquiry(
  inquiry: Pick<InquiryV2, 'anonymous' | 'recipient'> | null | undefined
): boolean {
  return Boolean(inquiry?.anonymous) && isAskAnonymouslyRoutedInquiry(inquiry);
}

/**
 * `title.type` is widened to an open string so the stamped `no_expert` value type-checks before
 * the API contract carries it. A real inquiry still satisfies this, so callers pass one unchanged.
 */
type NoExpertInquiryFields = Pick<InquiryV2, 'anonymous' | 'recipient' | 'origin'> & {
  title?: { type?: string } | null;
};

/**
 * True when an inquiry reached the Ask Anonymously routing user because no expert was selected
 * rather than because anonymity was requested. Both kinds share that inbox and are otherwise
 * indistinguishable.
 *
 * Zero experts selected is why the API routed here, so the destination is the count. `expertIds`
 * is never persisted, so the count cannot be read directly. Only `primum_support` origin is
 * excluded, since that marks a support-authored relay rather than a submission. A `primum` origin
 * means the submitter is internal, which is an ordinary submission and must still qualify.
 *
 * A stamped type wins, but its absence proves nothing, since only opted-in flows are stamped.
 * Gate callers on `isPrimumUser`.
 */
export function isNoExpertInquiry(inquiry: NoExpertInquiryFields | null | undefined): boolean {
  if (!inquiry) {
    return false;
  }

  if (inquiry.title?.type === 'no_expert') {
    return true;
  }

  if (!inquiry.recipient || inquiry.anonymous || inquiry.origin === 'primum_support') {
    return false;
  }

  return isAskAnonymouslyRoutedInquiry(inquiry);
}

/**
 * Expert snapshot from top-level `inquiry.title.expert` when the inquiry is anonymous and
 * Ask-Anonymously (Primum) routed. Does not read `groupInquiries`.
 *
 * Not gated on inbox grouping or submit fan-out — use when you only need the primary title
 * expert (e.g. shared fallback for relay copy when grouping UI or fan-out is off but the API
 * still includes `title.expert`).
 */
export function getAnonymousPrimumRoutedTitleExpert(
  inquiry?: InquiryWithDisplayMetadata | null
): InquiryTitleExpert | null {
  if (!inquiry?.anonymous || !inquiry.recipient) {
    return null;
  }

  if (!isAskAnonymously(getParticipantDisplayName(inquiry.recipient))) {
    return null;
  }

  const expert = inquiry.title?.expert;

  if (!expert) {
    return null;
  }

  return snapshotFromTitleExpertPayload(expert);
}

/**
 * Resolves the expert for Primum Ask-Anonymously relay UI (composer, captions, titles).
 * Tries top-level `title.expert`, then the first `groupInquiries[].title.expert`.
 *
 * Does not use the chat `recipient` prop from {@link useChat}: for internal viewers that prop is the
 * inquiry author (submitter), not the clinical expert.
 */
export function getAnonymousPrimumRoutedRelayExpert(
  inquiry: InquiryWithOptionalGroupInquiries | null | undefined
): InquiryTitleExpert | null {
  if (!inquiry?.anonymous || !isAnonymousPrimumRoutedInquiry(inquiry)) {
    return null;
  }

  const fromPayload = getAnonymousPrimumRoutedTitleExpert(inquiry);

  if (fromPayload) {
    return fromPayload;
  }

  for (const row of inquiry.groupInquiries ?? []) {
    const nestedExpert = row.title?.expert;

    if (nestedExpert) {
      return snapshotFromTitleExpertPayload(nestedExpert);
    }
  }

  return null;
}

/**
 * Expert snapshot from inquiry.title when Ask Anonymously routes the thread toward a specific expert.
 * This is gated by UI capability (grouped anonymous inquiry surfaces), not by submit fan-out behavior.
 * If title.expert is present in the inquiry payload, callers can opt to render the intended expert.
 */
export function getInquiryTitleExpert(
  inquiry?: InquiryWithDisplayMetadata | null,
  isTitleExpertDisplayEnabled = false
): InquiryTitleExpert | null {
  if (!isTitleExpertDisplayEnabled) {
    return null;
  }

  return getAnonymousPrimumRoutedTitleExpert(inquiry);
}

/**
 * True when a message in an anonymous inquiry was sent by the Ask Anonymously
 * routing user rather than the current user or an expert relay. These messages
 * render as Primum Support bubbles.
 */
export function isAnonymousPrimumRoutedMessage(
  message: ICustomMessage | undefined,
  context: {
    inquiry: Pick<InquiryV2, 'anonymous' | 'recipient'>;
    currentUserId: string;
    routingUserId: number;
  }
): boolean {
  if (!message) {
    return false;
  }

  return (
    isAnonymousPrimumRoutedInquiry(context.inquiry) &&
    !isSameUserId(message.user?._id, context.currentUserId) &&
    isSameUserId(message.user?._id, context.routingUserId) &&
    message.contentRole !== 'expert_relay' &&
    message.type !== 'expert_relay'
  );
}

export function getInquiryDisplayParticipant(
  inquiry: InquiryWithDisplayMetadata | null | undefined,
  fallbackParticipant: InquiryParticipant,
  isTitleExpertDisplayEnabled = false
) {
  return getInquiryTitleExpert(inquiry, isTitleExpertDisplayEnabled) ?? fallbackParticipant;
}
